<template>
  <!-- Standalone mode: full card with header -->
  <b-card v-if="!embedded" class="card-custom card-stretch" header-tag="header" no-body>
    <template v-slot:header>
      <div class="d-flex align-items-center justify-content-between w-100">
        <h6 class="mb-0">
          <b-icon icon="geo-alt" class="mr-1"></b-icon>
          Sensor Location
          <b-badge v-if="locations.length > 0" variant="light" class="ml-1">{{ locations.length }} point{{ locations.length !== 1 ? 's' : '' }}</b-badge>
        </h6>
        <div class="d-flex align-items-center" v-if="locations.length > 1" style="gap: 10px;">
          <b-form-checkbox v-model="showRoute" switch size="sm">
            <span class="font-size-xs">Route</span>
          </b-form-checkbox>
          <b-form-checkbox v-model="showArrows" switch size="sm" :disabled="!showRoute">
            <span class="font-size-xs">Arrows</span>
          </b-form-checkbox>
        </div>
      </div>
    </template>
    <b-card-body class="p-0" style="min-height: 220px;">
      <template v-if="!mapsReady">
        <div class="d-flex justify-content-center align-items-center" style="height: 220px;">
          <b-spinner variant="primary" small></b-spinner>
          <span class="ml-2 text-muted font-size-sm">Loading map…</span>
        </div>
      </template>
      <template v-else-if="locations.length === 0">
        <div class="d-flex justify-content-center align-items-center text-muted font-size-sm" style="height: 220px;">
          <b-icon icon="geo" class="mr-2"></b-icon>
          No location data available
        </div>
      </template>
      <template v-else>
        <GmapMap
          :center="mapCenter"
          :zoom="mapZoom"
          ref="tileMap"
          style="width: 100%; height: 280px; border-radius: 0 0 0.42rem 0.42rem;"
          :options="mapOptions"
          @zoom_changed="onZoomChanged"
        >
          <GmapPolyline v-if="showRoute && routePath.length > 1" :key="'route-' + showArrows + '-' + arrowOffset" :path="routePath" :options="routeOptions" />
          <GmapCircle v-if="latestPosition" :center="latestPosition" :radius="glowOuter" :options="{ fillColor: '#4FC3F7', fillOpacity: 0.12, strokeColor: '#4FC3F7', strokeOpacity: 0.25, strokeWeight: 1 }" />
          <GmapCircle v-if="latestPosition" :center="latestPosition" :radius="glowInner" :options="{ fillColor: '#0288D1', fillOpacity: 0.2, strokeColor: '#0288D1', strokeOpacity: 0.4, strokeWeight: 1 }" />
          <GmapMarker v-for="(loc, idx) in gmapMarkers" :key="'pin-' + idx" :position="loc.position" :icon="loc.icon" :title="loc.title" @click="selectedIdx = idx" />
          <GmapInfoWindow v-if="selectedIdx !== null && gmapMarkers[selectedIdx]" :position="gmapMarkers[selectedIdx].position" :opened="true" @closeclick="selectedIdx = null">
            <div style="font-size: 12px; max-width: 200px;">
              <strong>{{ gmapMarkers[selectedIdx].label }}</strong><br>
              <span class="text-muted">{{ gmapMarkers[selectedIdx].dateStr }}</span><br>
              <small class="text-muted">{{ gmapMarkers[selectedIdx].position.lat.toFixed(6) }}, {{ gmapMarkers[selectedIdx].position.lng.toFixed(6) }}</small>
            </div>
          </GmapInfoWindow>
        </GmapMap>
      </template>
    </b-card-body>
  </b-card>

  <!-- Embedded mode: just the map content, no card wrapper -->
  <div v-else>
    <!-- Route/Arrow toggles (slim bar) -->
    <div v-if="locations.length > 1" class="d-flex align-items-center px-3 py-2" style="gap: 10px; border-bottom: 1px solid #eee;">
      <b-form-checkbox v-model="showRoute" switch size="sm">
        <span class="font-size-xs">Route</span>
      </b-form-checkbox>
      <b-form-checkbox v-model="showArrows" switch size="sm" :disabled="!showRoute">
        <span class="font-size-xs">Arrows</span>
      </b-form-checkbox>
      <span class="ml-auto text-muted font-size-xs">{{ locations.length }} point{{ locations.length !== 1 ? 's' : '' }}</span>
    </div>
    <template v-if="!mapsReady">
      <div class="d-flex justify-content-center align-items-center" style="height: 220px;">
        <b-spinner variant="primary" small></b-spinner>
        <span class="ml-2 text-muted font-size-sm">Loading map…</span>
      </div>
    </template>
    <template v-else-if="locations.length === 0">
      <div class="d-flex justify-content-center align-items-center text-muted font-size-sm" style="height: 220px;">
        <b-icon icon="geo" class="mr-2"></b-icon>
        No location data available
      </div>
    </template>
    <template v-else>
      <GmapMap
        :center="mapCenter"
        :zoom="mapZoom"
        ref="tileMap"
        style="width: 100%; height: 280px; border-radius: 0 0 0.42rem 0.42rem;"
        :options="mapOptions"
        @zoom_changed="onZoomChanged"
      >
        <GmapPolyline v-if="showRoute && routePath.length > 1" :key="'route-' + showArrows + '-' + arrowOffset" :path="routePath" :options="routeOptions" />
        <GmapCircle v-if="latestPosition" :center="latestPosition" :radius="glowOuter" :options="{ fillColor: '#4FC3F7', fillOpacity: 0.12, strokeColor: '#4FC3F7', strokeOpacity: 0.25, strokeWeight: 1 }" />
        <GmapCircle v-if="latestPosition" :center="latestPosition" :radius="glowInner" :options="{ fillColor: '#0288D1', fillOpacity: 0.2, strokeColor: '#0288D1', strokeOpacity: 0.4, strokeWeight: 1 }" />
        <GmapMarker v-for="(loc, idx) in gmapMarkers" :key="'pin-' + idx" :position="loc.position" :icon="loc.icon" :title="loc.title" @click="selectedIdx = idx" />
        <GmapInfoWindow v-if="selectedIdx !== null && gmapMarkers[selectedIdx]" :position="gmapMarkers[selectedIdx].position" :opened="true" @closeclick="selectedIdx = null">
          <div style="font-size: 12px; max-width: 200px;">
            <strong>{{ gmapMarkers[selectedIdx].label }}</strong><br>
            <span class="text-muted">{{ gmapMarkers[selectedIdx].dateStr }}</span><br>
            <small class="text-muted">{{ gmapMarkers[selectedIdx].position.lat.toFixed(6) }}, {{ gmapMarkers[selectedIdx].position.lng.toFixed(6) }}</small>
          </div>
        </GmapInfoWindow>
      </GmapMap>
    </template>
  </div>
</template>

<script>
export default {
  name: 'SuitchTileMapWidget',
  props: {
    /** Array of { lat: Number, lng: Number, created_at: String } sorted ascending by time */
    locations: {
      type: Array,
      default: () => [],
    },
    /** When true, renders without card wrapper (for embedding inside another card) */
    embedded: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      mapsReady: false,
      showRoute: false,
      showArrows: false,
      arrowOffset: 0,
      arrowInterval: null,
      currentZoom: 15,
      selectedIdx: null,
    };
  },
  computed: {
    /** Convert locations to google.maps.LatLng-based markers with age-based styling */
    gmapMarkers() {
      if (!this.mapsReady || this.locations.length === 0) return [];
      const now = Date.now();
      const oldest = new Date(this.locations[0].created_at).getTime();
      const span = Math.max(now - oldest, 1);
      return this.locations.map((loc, idx) => {
        const t = new Date(loc.created_at).getTime();
        const age = (now - t) / span; // 0 = newest, 1 = oldest
        const isLatest = idx === this.locations.length - 1;
        const opacity = isLatest ? 1.0 : Math.max(0.3, 1 - age * 0.7);
        const scale = isLatest ? 8 : 5;
        const color = isLatest ? '#0288D1' : '#78909C';
        return {
          position: { lat: loc.lat, lng: loc.lng },
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: scale,
            fillColor: color,
            fillOpacity: opacity,
            strokeColor: '#fff',
            strokeWeight: isLatest ? 2.5 : 1,
          },
          title: this.formatDate(loc.created_at),
          label: isLatest ? 'Latest Position' : 'Position #' + (idx + 1),
          dateStr: this.formatDate(loc.created_at),
        };
      });
    },

    latestPosition() {
      if (this.locations.length === 0) return null;
      const last = this.locations[this.locations.length - 1];
      return { lat: last.lat, lng: last.lng };
    },

    mapCenter() {
      return this.latestPosition || { lat: 25.686613, lng: -100.316116 };
    },

    mapZoom() {
      if (this.locations.length <= 1) return 15;
      return 13;
    },

    routePath() {
      return this.locations.map(l => ({ lat: l.lat, lng: l.lng }));
    },

    routeOptions() {
      const opts = {
        strokeColor: '#0288D1',
        strokeOpacity: 0.7,
        strokeWeight: 2.5,
        geodesic: false,
      };
      if (this.showArrows && window.google && window.google.maps && window.google.maps.SymbolPath) {
        opts.icons = [{
          icon: {
            path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
            scale: 3.5,
            fillColor: '#0288D1',
            fillOpacity: 1,
            strokeColor: '#01579B',
            strokeWeight: 0.5,
          },
          offset: this.arrowOffset + '%',
          repeat: '80px',
        }];
      }
      return opts;
    },

    mapOptions() {
      return {
        disableDefaultUI: true,
        zoomControl: true,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: true,
        gestureHandling: 'cooperative',
        styles: [
          { featureType: 'poi', stylers: [{ visibility: 'off' }] },
          { featureType: 'transit', stylers: [{ visibility: 'off' }] },
        ],
      };
    },

    glowOuter() {
      const minZoom = 10, maxZoom = 20, minR = 500, maxR = 20;
      const z = Math.max(minZoom, Math.min(maxZoom, this.currentZoom));
      return minR - (minR - maxR) * ((z - minZoom) / (maxZoom - minZoom));
    },
    glowInner() {
      return this.glowOuter * 0.45;
    },
  },

  watch: {
    locations() {
      this.selectedIdx = null;
      this.$nextTick(() => this.fitBounds());
    },
    showArrows(val) {
      if (val && !this.arrowInterval) this.startArrowAnimation();
      if (!val && this.arrowInterval) this.stopArrowAnimation();
    },
  },

  methods: {
    formatDate(dateString) {
      try {
        const d = new Date(dateString);
        if (isNaN(d)) return 'N/A';
        return new Intl.DateTimeFormat(undefined, {
          day: '2-digit', month: '2-digit', year: 'numeric',
          hour: '2-digit', minute: '2-digit',
        }).format(d);
      } catch (e) {
        return 'N/A';
      }
    },

    onZoomChanged() {
      if (this.$refs.tileMap && this.$refs.tileMap.$mapObject) {
        this.currentZoom = this.$refs.tileMap.$mapObject.getZoom();
      }
    },

    fitBounds() {
      if (!this.mapsReady || !this.$refs.tileMap || this.locations.length === 0) return;
      const map = this.$refs.tileMap.$mapObject;
      if (!map) return;
      if (this.locations.length === 1) {
        map.setCenter(this.latestPosition);
        map.setZoom(15);
        return;
      }
      const bounds = new window.google.maps.LatLngBounds();
      this.locations.forEach(l => bounds.extend({ lat: l.lat, lng: l.lng }));
      map.fitBounds(bounds, { top: 30, bottom: 30, left: 30, right: 30 });
    },

    startArrowAnimation() {
      this.arrowInterval = setInterval(() => {
        this.arrowOffset = (this.arrowOffset + 0.167) % 100;
      }, 600);
    },
    stopArrowAnimation() {
      if (this.arrowInterval) clearInterval(this.arrowInterval);
      this.arrowInterval = null;
    },

    waitForGoogleMaps() {
      return new Promise((resolve, reject) => {
        if (window.google && window.google.maps) {
          resolve();
        } else {
          this.$gmapApiPromiseLazy()
            .then(() => resolve())
            .catch(error => reject(error));
        }
      });
    },
  },

  mounted() {
    this.waitForGoogleMaps()
      .then(() => {
        this.mapsReady = true;
        this.$nextTick(() => this.fitBounds());
      })
      .catch(() => {
        this.mapsReady = false;
      });
  },

  beforeDestroy() {
    this.stopArrowAnimation();
  },
};
</script>
